/**
 * Bloque 1 — Usuarios.
 *
 * La tabla de `users` es la autorización real del panel: Cloudflare Access dice
 * "esta persona es de laburen.com" y esta tabla dice qué ve. Por eso el rol se
 * edita en la fila, sin diálogo: es un cambio reversible de una sola dimensión y
 * un modal de confirmación por cada uno solo agrega clicks.
 */
import { ROLE_LABELS, roleSchema, type Role, type User } from '@ops/contracts'
import { useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { plural } from '../../lib/format'
import { useToast } from '../../shell/AppShell'
import { DataTable, Section, StatusPill, type Column } from '../../ui'
import { NotaFuente } from './Estados'
import { useUpdateUserRole } from './queries'

const ROLES: readonly Role[] = roleSchema.options

export interface UsuariosPanelProps {
  usuarios: readonly User[]
  id?: string
}

export function UsuariosPanel({ usuarios, id }: UsuariosPanelProps) {
  const { push } = useToast()
  const mutation = useUpdateUserRole()
  /** Fila en curso: se deshabilita solo esa, no la tabla entera. */
  const [enCurso, setEnCurso] = useState<string | null>(null)

  function cambiarRol(usuario: User, rol: Role) {
    setEnCurso(usuario.id)
    mutation.mutate(
      { userId: usuario.id, rol },
      {
        onSuccess: (guardado) => {
          push(
            'ok',
            `${guardado.nombre} ahora es ${ROLE_LABELS[guardado.rol]}`,
          )
        },
        // El mensaje es el de la API: "Es el único admin activo…" explica el
        // rechazo; "no se pudo guardar" no explica nada.
        onError: (error) => push('error', mensajeDeError(error)),
        onSettled: () => setEnCurso(null),
      },
    )
  }

  const porRol = ROLES.map((rol) => ({
    rol,
    cantidad: usuarios.filter((u) => u.rol === rol).length,
  }))
  const desactivadas = usuarios.filter((u) => !u.activo).length

  const columns: ReadonlyArray<Column<User>> = [
    {
      key: 'persona',
      header: 'Persona',
      rowHeader: true,
      sortValue: (u) => u.nombre,
      filterValue: (u) => `${u.nombre} ${u.email} ${ROLE_LABELS[u.rol]}`,
      render: (u) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{u.nombre}</span>
          <span className="truncate text-micro font-normal text-muted-strong">
            {u.email}
          </span>
        </span>
      ),
    },
    {
      key: 'rol',
      header: 'Rol',
      width: '190px',
      sortValue: (u) => ROLE_LABELS[u.rol],
      render: (u) => (
        <label className="flex items-center gap-1.5">
          <span className="sr-only">Rol de {u.nombre}</span>
          <select
            value={u.rol}
            disabled={enCurso === u.id}
            onChange={(evento) =>
              cambiarRol(u, roleSchema.parse(evento.target.value))
            }
            className="h-6 w-full rounded border border-border bg-surface px-1 text-meta text-text hover:border-n300 focus:outline-none focus:ring-2 focus:ring-accent-text disabled:cursor-wait disabled:bg-n100"
          >
            {ROLES.map((rol) => (
              <option key={rol} value={rol}>
                {ROLE_LABELS[rol]}
              </option>
            ))}
          </select>
          {enCurso === u.id ? (
            <span role="status" className="shrink-0 text-micro text-muted-strong">
              guardando…
            </span>
          ) : null}
        </label>
      ),
    },
    {
      key: 'acceso',
      header: 'Acceso',
      width: '160px',
      sortValue: (u) => (u.activo ? 'activa' : 'desactivada'),
      render: (u) =>
        u.activo ? (
          <StatusPill severidad="ok" size="sm">
            Habilitada
          </StatusPill>
        ) : (
          <StatusPill severidad="unknown" size="sm">
            Desactivada
          </StatusPill>
        ),
    },
  ]

  return (
    <Section
      id={id}
      kicker="BLOQUE 1"
      titulo="Quién entra y qué ve cada uno"
      bajada={
        <>
          <b>
            {usuarios.length} {plural(usuarios.length, 'cuenta', 'cuentas')}
          </b>{' '}
          de laburen.com.{' '}
          {porRol
            .map((p) => `${p.cantidad} ${ROLE_LABELS[p.rol].toLowerCase()}`)
            .join(' · ')}
          {desactivadas > 0 ? (
            <>
              , y <b>{desactivadas}</b>{' '}
              {plural(desactivadas, 'desactivada', 'desactivadas')}, que no{' '}
              {plural(desactivadas, 'entra', 'entran')} aunque{' '}
              {plural(desactivadas, 'siga', 'sigan')} habilitada
              {plural(desactivadas, '', 's')} en Cloudflare Access
            </>
          ) : null}
          . El rol se resuelve por email en cada request: cambiarlo acá cambia lo
          que la persona ve en su próxima navegación, no lo que ya tiene en
          pantalla.
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={usuarios}
        rowKey={(u) => u.id}
        density="compacta"
        filtroPlaceholder="Nombre, email o rol"
        emptyMessage="No hay ninguna cuenta cargada. Sin cuentas nadie puede entrar al panel."
        caption={
          <NotaFuente>
            Tabla <code>users</code> de D1. Es la autorización real: la sidebar se
            filtra sola por rol, pero el que decide el 403 es el servidor.
          </NotaFuente>
        }
        footer={
          <>
            {usuarios.length} {plural(usuarios.length, 'cuenta', 'cuentas')} ·{' '}
            {porRol.map((p) => `${p.cantidad} ${ROLE_LABELS[p.rol]}`).join(' · ')}
            {desactivadas > 0
              ? ` · ${desactivadas} ${plural(desactivadas, 'desactivada', 'desactivadas')}`
              : ''}
          </>
        }
      />
    </Section>
  )
}
